// Single-item swap catalog — change ONE garment/accessory while keeping pose & background.
import type { SingleItemV2 } from "./style-types";

import whiteTee from "@/assets/single-items/white-tee.webp";
import blackHoodie from "@/assets/single-items/black-hoodie.webp";
import whiteShirt from "@/assets/single-items/white-shirt.webp";
import greySweatshirt from "@/assets/single-items/grey-sweatshirt.webp";
import pinkCardigan from "@/assets/single-items/pink-cardigan.webp";
import creamSweater from "@/assets/single-items/cream-sweater.webp";
import floralDress from "@/assets/single-items/floral-dress.webp";

import blueJeans from "@/assets/single-items/blue-jeans.webp";
import blackTrousers from "@/assets/single-items/black-trousers.webp";
import beigeChinos from "@/assets/single-items/beige-chinos.webp";
import navyChinos from "@/assets/single-items/navy-chinos.webp";
import denimSkirt from "@/assets/single-items/denim-skirt.webp";

import denimJacket from "@/assets/single-items/denim-jacket.webp";
import beigeBlazer from "@/assets/single-items/beige-blazer.webp";
import leatherJacket from "@/assets/single-items/leather-jacket.webp";
import trenchCoat from "@/assets/single-items/trench-coat.webp";

import whiteSneakers from "@/assets/single-items/white-sneakers.webp";
import blackSneakers from "@/assets/single-items/black-sneakers.webp";
import blackBoots from "@/assets/single-items/black-boots.webp";
import brownAnkleBoots from "@/assets/single-items/brown-ankle-boots.webp";
import brownLoafers from "@/assets/single-items/brown-loafers.webp";
import nudePumps from "@/assets/single-items/nude-pumps.webp";
import goldHeels from "@/assets/single-items/gold-heels.webp";

import blackTote from "@/assets/single-items/black-tote.webp";
import brownCrossbody from "@/assets/single-items/brown-crossbody.webp";
import miniBackpack from "@/assets/single-items/mini-backpack.webp";
import redShoulderBag from "@/assets/single-items/red-shoulder-bag.webp";
import strawTote from "@/assets/single-items/straw-tote.webp";
import nudeHandbag from "@/assets/single-items/nude-handbag.webp";
import blackChainBag from "@/assets/single-items/black-chain-bag.webp";

import silverWatch from "@/assets/single-items/silver-watch.webp";
import goldWatch from "@/assets/single-items/gold-watch.webp";
import smartwatch from "@/assets/single-items/smartwatch.webp";
import roseGoldWatch from "@/assets/single-items/rose-gold-watch.webp";

import goldNecklace from "@/assets/single-items/gold-necklace.webp";
import silverEarrings from "@/assets/single-items/silver-earrings.webp";
import jhumkaEarrings from "@/assets/single-items/jhumka-earrings.webp";
import pearlNecklace from "@/assets/single-items/pearl-necklace.webp";
import goldStuds from "@/assets/single-items/gold-studs.webp";
import goldBracelet from "@/assets/single-items/gold-bracelet.webp";

import baseballCap from "@/assets/single-items/baseball-cap.webp";
import sunglasses from "@/assets/single-items/sunglasses.webp";
import roundSunglasses from "@/assets/single-items/round-sunglasses.webp";
import silkScarf from "@/assets/single-items/silk-scarf.webp";
import fedoraHat from "@/assets/single-items/fedora-hat.webp";
import brownBelt from "@/assets/single-items/brown-belt.webp";
import creamBeanie from "@/assets/single-items/cream-beanie.webp";

// New additions
import navyTee from "@/assets/single-items/navy-tee.webp";
import oliveBomber from "@/assets/single-items/olive-bomber.webp";
import greyOvercoat from "@/assets/single-items/grey-overcoat.webp";
import blackPalazzo from "@/assets/single-items/black-palazzo.webp";
import creamPleatedSkirt from "@/assets/single-items/cream-pleated-skirt.webp";
import blackDress from "@/assets/single-items/black-dress.webp";
import brownHighTops from "@/assets/single-items/brown-high-tops.webp";
import tanOxfords from "@/assets/single-items/tan-oxfords.webp";
import nudeFlats from "@/assets/single-items/nude-flats.webp";
import blackBriefcase from "@/assets/single-items/black-briefcase.webp";
import beigeDuffel from "@/assets/single-items/beige-duffel.webp";
import diamondBracelet from "@/assets/single-items/diamond-bracelet.webp";
import goldHoops from "@/assets/single-items/gold-hoops.webp";
import leatherStrapWatch from "@/assets/single-items/leather-strap-watch.webp";
import blackBeanie from "@/assets/single-items/black-beanie.webp";

// Indian + extras
import jhumkasPearl from "@/assets/single-items/jhumkas-pearl.webp";
import juttisBeige from "@/assets/single-items/juttis-beige.webp";
import mojariBrown from "@/assets/single-items/mojari-brown.webp";
import kolhapuriSandals from "@/assets/single-items/kolhapuri-sandals.webp";
import banarasiDupatta from "@/assets/single-items/banarasi-dupatta.webp";
import maangTikka from "@/assets/single-items/maang-tikka.webp";
import mangalsutra from "@/assets/single-items/mangalsutra.webp";
import banglesSet from "@/assets/single-items/bangles-set.webp";
import nehruJacket from "@/assets/single-items/nehru-jacket.webp";
import creamKurta from "@/assets/single-items/cream-kurta.webp";
import chunkyBoots from "@/assets/single-items/chunky-boots.webp";

export type SingleItem = SingleItemV2;

export const SINGLE_ITEMS: SingleItemV2[] = [
  // TOPS
  { id: "white-tee", type: "top", label: "White Tee", image: whiteTee, genders: ["men", "women"],
    prompt: "Plain white crewneck cotton t-shirt, regular fit." },
  { id: "black-hoodie", type: "top", label: "Black Hoodie", image: blackHoodie, genders: ["men", "women"],
    prompt: "Black pullover hoodie with kangaroo pocket, relaxed fit." },
  { id: "white-shirt", type: "top", label: "Oxford Shirt", image: whiteShirt, genders: ["men", "women"],
    prompt: "Crisp white classic oxford button-down shirt, long sleeves." },
  { id: "grey-sweatshirt", type: "top", label: "Grey Sweatshirt", image: greySweatshirt, genders: ["men", "women"],
    prompt: "Heather grey crewneck cotton sweatshirt, relaxed fit." },
  { id: "cream-sweater", type: "top", label: "Cream Knit", image: creamSweater, genders: ["men", "women"],
    prompt: "Soft cream cable-knit oversized sweater." },
  { id: "pink-cardigan", type: "top", label: "Pink Cardigan", image: pinkCardigan, genders: ["women"],
    prompt: "Soft pastel pink ribbed knit cardigan with mother-of-pearl buttons." },
  { id: "floral-dress", type: "top", label: "Floral Dress", image: floralDress, genders: ["women"],
    prompt: "Soft yellow floral midi sundress with thin spaghetti straps and small white daisies." },
  { id: "navy-tee", type: "top", label: "Navy Tee", image: navyTee, genders: ["men", "women"],
    prompt: "Plain navy blue cotton crewneck t-shirt, regular fit." },
  { id: "black-dress", type: "top", label: "Little Black Dress", image: blackDress, genders: ["women"],
    prompt: "Classic sleeveless black A-line cocktail mini dress, knee length." },

  // BOTTOMS
  { id: "blue-jeans", type: "bottom", label: "Indigo Jeans", image: blueJeans, genders: ["men", "women"],
    prompt: "Slim-fit dark indigo blue denim jeans." },
  { id: "black-trousers", type: "bottom", label: "Black Trousers", image: blackTrousers, genders: ["men", "women"],
    prompt: "Tailored black dress trousers, slim straight cut." },
  { id: "beige-chinos", type: "bottom", label: "Beige Chinos", image: beigeChinos, genders: ["men", "women"],
    prompt: "Beige cotton chino trousers, slim fit." },
  { id: "navy-chinos", type: "bottom", label: "Navy Chinos", image: navyChinos, genders: ["men", "women"],
    prompt: "Dark navy blue tailored chino trousers, slim straight cut." },
  { id: "denim-skirt", type: "bottom", label: "Denim Skirt", image: denimSkirt, genders: ["women"],
    prompt: "High-waist light blue A-line denim midi skirt." },
  { id: "black-palazzo", type: "bottom", label: "Black Palazzo", image: blackPalazzo, genders: ["women"],
    prompt: "Black wide-leg high-waist palazzo trousers in flowy fabric." },
  { id: "cream-pleated-skirt", type: "bottom", label: "Cream Pleats", image: creamPleatedSkirt, genders: ["women"],
    prompt: "Cream pleated A-line midi skirt." },

  // OUTERWEAR
  { id: "denim-jacket", type: "outerwear", label: "Denim Jacket", image: denimJacket, genders: ["men", "women"],
    prompt: "Light-wash blue denim trucker jacket, classic fit." },
  { id: "beige-blazer", type: "outerwear", label: "Beige Blazer", image: beigeBlazer, genders: ["men", "women"],
    prompt: "Beige tailored single-button blazer." },
  { id: "leather-jacket", type: "outerwear", label: "Leather Jacket", image: leatherJacket, genders: ["men", "women"],
    prompt: "Black biker-style leather jacket with asymmetric zip and silver hardware." },
  { id: "trench-coat", type: "outerwear", label: "Trench Coat", image: trenchCoat, genders: ["men", "women"],
    prompt: "Beige double-breasted classic trench coat with belt." },
  { id: "olive-bomber", type: "outerwear", label: "Olive Bomber", image: oliveBomber, genders: ["men", "women"],
    prompt: "Olive green army-style cotton bomber jacket with ribbed cuffs and collar." },
  { id: "grey-overcoat", type: "outerwear", label: "Grey Overcoat", image: greyOvercoat, genders: ["men", "women"],
    prompt: "Light grey wool blend long single-breasted overcoat with notched lapels." },

  // SHOES
  { id: "white-sneakers", type: "shoes", label: "White Sneakers", image: whiteSneakers, genders: ["men", "women"],
    prompt: "Clean minimalist white leather low-top sneakers." },
  { id: "black-sneakers", type: "shoes", label: "Black Canvas", image: blackSneakers, genders: ["men", "women"],
    prompt: "Classic low-top black canvas sneakers with black rubber sole." },
  { id: "black-boots", type: "shoes", label: "Black Boots", image: blackBoots, genders: ["men", "women"],
    prompt: "Black leather Chelsea ankle boots with elastic side panels." },
  { id: "brown-ankle-boots", type: "shoes", label: "Brown Ankle Boots", image: brownAnkleBoots, genders: ["women"],
    prompt: "Brown leather women's ankle boots with low block heel." },
  { id: "brown-loafers", type: "shoes", label: "Brown Loafers", image: brownLoafers, genders: ["men", "women"],
    prompt: "Polished brown leather penny loafers." },
  { id: "nude-pumps", type: "shoes", label: "Nude Pumps", image: nudePumps, genders: ["women"],
    prompt: "Beige nude pointed-toe block-heel pumps, mid heel." },
  { id: "gold-heels", type: "shoes", label: "Gold Heels", image: goldHeels, genders: ["women"],
    prompt: "Gold metallic strappy stiletto sandals with thin ankle strap." },
  { id: "brown-high-tops", type: "shoes", label: "Brown High-Tops", image: brownHighTops, genders: ["men", "women"],
    prompt: "Brown suede high-top sneakers with white rubber sole and laces." },
  { id: "tan-oxfords", type: "shoes", label: "Tan Oxfords", image: tanOxfords, genders: ["men"],
    prompt: "Polished tan brown leather oxford lace-up dress shoes." },
  { id: "nude-flats", type: "shoes", label: "Nude Flats", image: nudeFlats, genders: ["women"],
    prompt: "Beige nude pointed-toe leather ballet flats." },

  // BAGS
  { id: "black-tote", type: "bag", label: "Black Tote", image: blackTote, genders: ["women"],
    prompt: "Structured black leather tote handbag with top handles." },
  { id: "brown-crossbody", type: "bag", label: "Brown Crossbody", image: brownCrossbody, genders: ["men", "women"],
    prompt: "Compact tan/brown leather crossbody messenger bag with adjustable strap." },
  { id: "mini-backpack", type: "bag", label: "Mini Backpack", image: miniBackpack, genders: ["women"],
    prompt: "Small pastel mini backpack with two thin straps and a front pocket." },
  { id: "red-shoulder-bag", type: "bag", label: "Red Shoulder", image: redShoulderBag, genders: ["women"],
    prompt: "Small bright red leather quilted shoulder bag with a thin gold chain strap." },
  { id: "straw-tote", type: "bag", label: "Straw Tote", image: strawTote, genders: ["women"],
    prompt: "Natural beige woven straw tote beach bag with twin handles and a small white tassel." },
  { id: "nude-handbag", type: "bag", label: "Nude Handbag", image: nudeHandbag, genders: ["women"],
    prompt: "Small structured beige nude leather top-handle handbag with gold hardware." },
  { id: "black-chain-bag", type: "bag", label: "Black Chain", image: blackChainBag, genders: ["women"],
    prompt: "Small black quilted leather chain shoulder bag with silver chain strap." },
  { id: "black-briefcase", type: "bag", label: "Briefcase", image: blackBriefcase, genders: ["men"],
    prompt: "Black leather briefcase laptop bag with top handle and detachable shoulder strap." },
  { id: "beige-duffel", type: "bag", label: "Weekender", image: beigeDuffel, genders: ["men", "women"],
    prompt: "Beige canvas weekender duffel bag with cognac brown leather handles and shoulder strap." },

  // WATCHES
  { id: "silver-watch", type: "watch", label: "Silver Watch", image: silverWatch, genders: ["men", "women"],
    prompt: "Stainless steel silver wristwatch with white dial and metal link bracelet." },
  { id: "gold-watch", type: "watch", label: "Gold Watch", image: goldWatch, genders: ["men", "women"],
    prompt: "Polished gold-tone wristwatch with cream dial and gold link bracelet." },
  { id: "smartwatch", type: "watch", label: "Smartwatch", image: smartwatch, genders: ["men", "women"],
    prompt: "Modern black square smartwatch with black silicone sport band and digital display." },
  { id: "rose-gold-watch", type: "watch", label: "Rose Gold", image: roseGoldWatch, genders: ["women"],
    prompt: "Minimalist rose-gold wristwatch with mesh strap and pink mother-of-pearl dial." },
  { id: "leather-strap-watch", type: "watch", label: "Leather Strap", image: leatherStrapWatch, genders: ["men"],
    prompt: "Classic men's silver chronograph wristwatch with black leather strap and white dial." },

  // JEWELLERY
  { id: "gold-necklace", type: "jewellery", label: "Gold Necklace", image: goldNecklace, genders: ["women"],
    prompt: "Delicate gold chain necklace with a small pendant." },
  { id: "pearl-necklace", type: "jewellery", label: "Pearl Strand", image: pearlNecklace, genders: ["women"],
    prompt: "Single-strand white pearl necklace, classic and elegant." },
  { id: "silver-earrings", type: "jewellery", label: "Silver Hoops", image: silverEarrings, genders: ["women"],
    prompt: "Medium silver hoop earrings, polished finish." },
  { id: "gold-studs", type: "jewellery", label: "Gold Studs", image: goldStuds, genders: ["women"],
    prompt: "Small gold stud earrings with a single tiny diamond each." },
  { id: "jhumka-earrings", type: "jewellery", label: "Gold Jhumkas", image: jhumkaEarrings, genders: ["women"],
    prompt: "Traditional Indian gold jhumka earrings with intricate filigree and small pearl drops." },
  { id: "gold-bracelet", type: "jewellery", label: "Gold Bracelet", image: goldBracelet, genders: ["women"],
    prompt: "Thin gold chain bracelet with a small heart charm." },
  { id: "diamond-bracelet", type: "jewellery", label: "Diamond Bangle", image: diamondBracelet, genders: ["women"],
    prompt: "Sparkling silver diamond-encrusted tennis bracelet." },
  { id: "gold-hoops", type: "jewellery", label: "Gold Hoops", image: goldHoops, genders: ["women"],
    prompt: "Small gold hoop earrings, polished finish." },

  // ACCESSORIES
  { id: "baseball-cap", type: "accessory", label: "Baseball Cap", image: baseballCap, genders: ["men", "women"],
    prompt: "Plain black cotton baseball cap with curved brim." },
  { id: "fedora-hat", type: "accessory", label: "Brown Fedora", image: fedoraHat, genders: ["men", "women"],
    prompt: "Wide-brim brown felt fedora hat with a thin black satin band." },
  { id: "cream-beanie", type: "accessory", label: "Cream Beanie", image: creamBeanie, genders: ["men", "women"],
    prompt: "Soft cream knitted ribbed beanie hat." },
  { id: "sunglasses", type: "accessory", label: "Sunglasses", image: sunglasses, genders: ["men", "women"],
    prompt: "Black classic wayfarer-style sunglasses with dark lenses." },
  { id: "round-sunglasses", type: "accessory", label: "Round Shades", image: roundSunglasses, genders: ["men", "women"],
    prompt: "Small round gold metal-frame sunglasses with subtle brown gradient lenses." },
  { id: "silk-scarf", type: "accessory", label: "Silk Scarf", image: silkScarf, genders: ["women"],
    prompt: "Floral-print silk neck scarf tied loosely around the neck." },
  { id: "brown-belt", type: "accessory", label: "Brown Belt", image: brownBelt, genders: ["men", "women"],
    prompt: "Brown leather classic dress belt with a polished silver buckle." },
  { id: "black-beanie", type: "accessory", label: "Black Beanie", image: blackBeanie, genders: ["men", "women"],
    prompt: "Black ribbed wool knit beanie hat with folded cuff." },

  // INDIAN ITEMS
  { id: "cream-kurta", type: "top", label: "Cream Kurta", image: creamKurta, genders: ["men"],
    prompt: "Cream silk men's kurta tunic with subtle gold thread embroidery on the placket and cuffs, mandarin collar, knee length." },
  { id: "nehru-jacket", type: "outerwear", label: "Nehru Jacket", image: nehruJacket, genders: ["men"],
    prompt: "Tailored navy blue Nehru jacket with mandarin collar, single-breasted with five buttons, slim fit." },
  { id: "juttis-beige", type: "shoes", label: "Embroidered Juttis", image: juttisBeige, genders: ["men", "women"],
    prompt: "Traditional Indian beige flat juttis with intricate gold thread embroidery, slip-on style." },
  { id: "mojari-brown", type: "shoes", label: "Brown Mojari", image: mojariBrown, genders: ["men"],
    prompt: "Traditional Indian brown leather mojari shoes with subtle embroidered detailing and a slight curled toe." },
  { id: "kolhapuri-sandals", type: "shoes", label: "Kolhapuri Sandals", image: kolhapuriSandals, genders: ["men", "women"],
    prompt: "Traditional handcrafted tan brown Kolhapuri leather sandals with woven straps, flat sole." },
  { id: "banarasi-dupatta", type: "accessory", label: "Banarasi Dupatta", image: banarasiDupatta, genders: ["women"],
    prompt: "Red Banarasi silk dupatta with intricate gold zari motifs and tasselled border, draped elegantly." },
  { id: "jhumkas-pearl", type: "jewellery", label: "Pearl Jhumkas", image: jhumkasPearl, genders: ["women"],
    prompt: "Traditional Indian gold jhumka earrings with intricate filigree and pearl drops." },
  { id: "maang-tikka", type: "jewellery", label: "Maang Tikka", image: maangTikka, genders: ["women"],
    prompt: "Ornate gold Indian maang tikka forehead jewellery with kundan stones, ruby and emerald accents and pearl drops." },
  { id: "mangalsutra", type: "jewellery", label: "Mangalsutra", image: mangalsutra, genders: ["women"],
    prompt: "Traditional Indian gold mangalsutra necklace with black beaded chain and a small gold pendant." },
  { id: "bangles-set", type: "jewellery", label: "Bangles Set", image: banglesSet, genders: ["women"],
    prompt: "Stack of traditional Indian gold and red glass bangles, worn together as a full set." },

  // EXTRAS
  { id: "chunky-boots", type: "shoes", label: "Chunky Boots", image: chunkyBoots, genders: ["men", "women"],
    prompt: "Black chunky platform combat lace-up boots with thick lugged sole." },
];
